<template>
    <div class="d-flex justify-content-end actions" v-if="requestType">
        <div v-if="requestType === 'received'">
            <div class="d-lg-flex d-none">
                <Button 
                    @click.stop.native="declineRequest" 
                    style="margin-left: 24px; margin-right: 8px" 
                    type="decline"
                    class="decline-received-request">
                        Decline
                </Button>
                <Button 
                    @click.stop.native="acceptRequest" 
                    type="accept"
                    class="confirm-received-request">
                        Confirm
                </Button>
            </div>
            <div class="d-lg-none d-flex">
                <button @click="declineRequest" class="p-0">
                    <Icon icon="decline-icon-dashboard"/>
                </button>
                <button @click="acceptRequest" class="p-0 ml-3">
                    <Icon icon="accept-icon-dashboard"/>
                </button>
            </div>
        </div>
        <Button 
            v-else 
            @click.stop.native="cancelRequest"
            type="decline"
            class="cancel-sent-request">
                Cancel
        </Button>
    </div>
</template>

<script>
import Button from '../../Button'
import Icon from '../../../../../components/Theme/Icon'
import {dashboardUtils} from '../../../../../core/utils'
import { message } from 'ant-design-vue'
import {requestsUseCase, updatesUseCase} from '../../../../../core/usecases'
import {db} from '../../../../../firebase'
import {mapState} from 'vuex'

export default {
    name: 'Actions',
    components: {
        Button,
        Icon
    },
    data(){
        return {
            request: null
        }
    },
    props: {
        requestData: Object,
        requestsList: Array,
        requestId: String
    },
    async mounted(){

        this.request = {...this.requestData}

        if(this.requestId) {
            const unsubscribe = await requestsUseCase.getRequest(this.requestId).onSnapshot(request => {

                const data = request.data()

                this.request = {
                    ...data,
                    side: data.receiver.uid === this.user.userId ? 'received' : 'sent'
                }

                this.$emit('setRequestData', data.id, {
                    key: 'status',
                    value: data.status
                })

                if(data.status !== 'pending') {
                    unsubscribe()
                }
            })
        }
    },
    computed: {
        ...mapState([
            'user'
        ]),
        requestType(){
            if(this.$route.name === 'chat') {
                return this.request && this.request.side
            }
            return this.$route.query.type || 'received'
        },
        daysAfterReceiving() {
            return dashboardUtils.daysPassed(this.request.created_date)
        },
        location(){
            switch(this.$route.name){
                case 'requests':
                    return 'Dashboard'
                case 'chat':
                    return 'Chat'
            }
        },
    },
    methods: {
        containsExpiredDate() {
            const hasExpiredDate = dashboardUtils.isExpired(this.request.receiver.update_date) || dashboardUtils.isExpired(this.request.sender.update_date)
            if(hasExpiredDate){
                message.error('The date has expired')
                // this.cancelRequest({autoCancelled: true});
                this.setAsExpired()
            }
            return hasExpiredDate;
        },

        declineRequest() {

            if(this.containsExpiredDate()){
                return;
            }

            this.$emit('setRequestData', this.request.id, {
                key: 'status',
                value: 'declined'
            })
            
            requestsUseCase.setRequestStatus(this.request.id, 'declined')

            this.$ga.event('CpRequest', 'Decline Request', `
                ReceiverCampaign: ${this.request.receiver.title}, 
                SenderCampaign: ${this.request.sender.title},
                DaysAfterReceiving: ${this.daysAfterReceiving},
                Location: ${this.location}
            `);

            this.$sendMixpanelEvent('Decline Request', {
                'Receiver Campaign': this.request.receiver.title, 
                'Sender Campaign': this.request.sender.title,
                'Days after receiving': this.daysAfterReceiving,
                'Location': this.location
            });
            this.$incrementMixpanelPeopleProperty('Declined Requests')
        },

        setAsExpired(){
            
            this.$emit('setRequestData', this.request.id, {
                key: 'status',
                value: 'expired'
            })
            requestsUseCase.setRequestStatus(this.request.id, 'expired')

            this.$ga.event('CpRequest', 'Expired Request', `
                ReceiverCampaign: ${this.request.receiver.title}, 
                SenderCampaign: ${this.request.sender.title},
                DaysAfterReceiving: ${this.daysAfterReceiving},
                Location: ${this.location}
            `);

            this.$sendMixpanelEvent('Expired Request', {
                'Receiver Campaign': this.request.receiver.title, 
                'Sender Campaign': this.request.sender.title,
                'Days after receiving': this.daysAfterReceiving,
                'Location': this.location
            });

            this.$incrementMixpanelPeopleProperty('Expired Requests')
        },

        cancelRequest(param){

            this.$emit('setRequestData', this.request.id, {
                key: 'status',
                value: 'cancelled'
            })
            requestsUseCase.setRequestStatus(this.request.id, 'cancelled')

            this.$ga.event('CpRequest', 'Cancel Request', `
                ReceiverCampaign: ${this.request.receiver.title}, 
                SenderCampaign: ${this.request.sender.title},
                ${param.autoCancelled ? 'DaysAfterReceiving' : 'DaysAfterSending'}: ${this.daysAfterReceiving},
                AutoCancelled: ${!!param.autoCancelled},
                Location: ${this.location}
            `);

            this.$sendMixpanelEvent('Cancel Request', {
                'Receiver Campaign': this.request.receiver.title, 
                'Sender Campaign': this.request.sender.title,
                [param.autoCancelled ? 'Days after receiving' : 'Days after sending']: this.daysAfterReceiving,
                'Auto Cancelled': !!param.autoCancelled,
                'Location': this.location
            });
            this.$incrementMixpanelPeopleProperty('Cancelled Requests')
        },
        acceptRequest() {

            if(this.containsExpiredDate()){
                return;
            }

            // update update dates
            Promise.all(
                [
                    this.request.sender.update_date_id,
                    this.request.receiver.update_date_id,
                ].map(
                    id => db.collection('updates').doc(id).get()
                )
            ).then(
                ([senderUpdates, receiverUpdates]) => {
                    if(!senderUpdates || !receiverUpdates || !senderUpdates.data() || !receiverUpdates.data()) {
                        this.cancelRequest({autoCancelled: true});
                        throw new Error('Something went wrong')
                    }

                    this.$emit('setRequestData', this.request.id, {
                        key: 'status',
                        value: 'accepted'
                    })


                    const senderSpots = [...senderUpdates.data().spots]
                    const receiverSpots = [...receiverUpdates.data().spots]

                    // make sure spots are free
                    if (
                        senderSpots[this.request.sender.spot_position] ||
                        receiverSpots[this.request.receiver.spot_position]
                    ) {

                        this.cancelRequest({autoCancelled: true});
                        throw new Error('The spot is already taken!')
                    }

                    requestsUseCase.setRequestStatus(this.request.id, 'accepted')

                    this.$ga.event('CpRequest', 'Accept Request', `
                        ReceiverCampaign: ${this.request.receiver.title}, 
                        DaysAfterReceiving: ${this.daysAfterReceiving},
                        Location: ${this.location}
                    `)

                    this.$sendMixpanelEvent('Accept Request', {
                        'Receiver Campaign': this.request.receiver.title, 
                        'Days after receiving': this.daysAfterReceiving,
                        'Location': this.location
                    });

                    this.$incrementMixpanelPeopleProperty('Accepted Requests')

                    senderSpots[this.request.sender.spot_position] = {
                        uid: this.request.receiver.uid,
                        cimage: this.request.receiver.image,
                        cname: this.request.receiver.title,
                        cpid: this.request.receiver.campaign,
                    }
                    receiverSpots[this.request.receiver.spot_position] = {
                        uid: this.request.sender.uid,
                        cimage: this.request.sender.image,
                        cname: this.request.sender.title,
                        cpid: this.request.sender.campaign,
                    }
                    const batch = db.batch()
                    batch.update(senderUpdates.ref, { spots: senderSpots })
                    batch.update(receiverUpdates.ref, { spots: receiverSpots })
                    batch.commit()

                    // cancel other pending requests with the same spot (only front end part)
                    if(this.requestsList) {
                        this.requestsList.forEach(item => {
                            if(
                                // case 1: when receiver has other request on this spot
                                (item.receiver.spot_position === this.request.receiver.spot_position && item.receiver.update_date_id === this.request.receiver.update_date_id ||
                                // case 2: when sender has other request on this spot
                                item.sender.spot_position === this.request.sender.spot_position && item.sender.update_date_id === this.request.sender.update_date_id ||
                                // case 3: when receiver and sender are the same and one of update date's is the same
                                item.sender.campaign === this.request.sender.campaign && item.receiver.campaign === this.request.receiver.campaign && (
                                    item.sender.update_date_id === this.request.sender.update_date_id || item.receiver.update_date_id === this.request.receiver.update_date_id
                                )) && item.id !== this.request.id && item.status === 'pending'
                            ){
                                this.$emit('setRequestData', item.id, {
                                    key: 'status',
                                    value: 'cancelled'
                                })
                            }
                        })
                    }
                }
            )
            .catch(
                (e) => {
                    console.log(e, e.message)
                    message.error(e.message)
                }
            )
        },
    }
}
</script>

<style lang="scss" scoped>
div.actions {
    padding-right: 16px;
}
@media (max-width: 992px) {
    div.actions {
        padding-right: 0;
    }
}
</style>